import React, { JSX, useState } from 'react';
import { ListRowItem, SimpleListProps } from './types';
import { ScrollerService, useTranslation } from '@ibe/components';
import fallback from '../../Translations/generated/simple-list.de.json';
import { Button } from 'reactstrap';
import Keys from '../../Translations/generated/simple-list.de.json.keys';
import { listSegmentation } from './additionals';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Element as ScrollTo } from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

type TableBodyProps<L extends ListRowItem> = {
  listRows: SimpleListProps<L>['listRows'];
  itemsInRow?: number;
  isDesktop?: boolean;
  className?: string;
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
  customRowError?: () => JSX.Element;
  errorRowColSpanDiff?: number;
};

export const TableBody = <L extends ListRowItem>(props: TableBodyProps<L>): JSX.Element => {
  const { listRows, className, ...listRowProps } = props;
  return (
    <TransitionGroup
      component="tbody"
      className={`simple-list__transition${className ? ' ' + className : ''}`}
    >
      {listRows.map((listRow, index) => (
        <CSSTransition key={listRow.id} nodeRef={listRow.nodeRef} timeout={400} classNames="fade">
          <ListRow
            {...listRowProps}
            listRows={listRows}
            listRow={listRow}
            key={listRow.id}
            index={index}
          />
        </CSSTransition>
      ))}
    </TransitionGroup>
  );
};

type ListRowProps<L extends ListRowItem> = {
  listRows: SimpleListProps<L>['listRows'];
  itemsInRow?: number;
  isDesktop?: boolean;
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
  customRowError?: () => JSX.Element;
  errorRowColSpanDiff?: number;
};

const ListRow = <L extends ListRowItem>(
  props: ListRowProps<L> & { listRow: TableBodyProps<L>['listRows'][0]; index: number }
) => {
  const {
    listRow,
    listRows,
    isDesktop,
    itemsInRow,
    errorRowColSpanDiff,
    onSelect: onSelectProp,
    checkAvailability,
    customRowError,
    index
  } = props;
  const { ErrorResponse, isAvailable, onSelect, t, isLoading } = useTableRowHook({
    onSelect: onSelectProp,
    checkAvailability,
    customRowError,
    index
  });

  const segments = listSegmentation(listRow.item, itemsInRow, isDesktop);
  const lastSegmentIndex = segments.length - 1;

  const selectButton = (
    <Button
      color="primary"
      disabled={isLoading}
      onClick={(): Promise<void> =>
        onSelect(listRow.id, listRow.code, listRow.groupSize ?? '', listRows.indexOf(listRow))
      }
    >
      {t(Keys.select)}
    </Button>
  );

  const baseColSpan = Object.keys(listRow.item).length + (errorRowColSpanDiff ?? 0) || 5;
  const errorRowColSpan = isDesktop ? Math.max(1, baseColSpan - 1) : baseColSpan;

  return (
    <>
      <tr className={listRow.specialRow ? `simple-list__body__special ${listRow.groupSize}` : ''}>
        {segments.map((item, segmentIndex) => {
          const isLastSegment = segmentIndex === lastSegmentIndex;
          const mergeButtonIntoCell = isDesktop && isLastSegment;
          return (
            <td
              key={item.id}
              className={`${item.className ?? ''}${
                mergeButtonIntoCell ? ' simple-list__body__price-select' : ''
              }`}
            >
              {Object.keys(item.list).map(key =>
                item.list[key].imgSrc ? (
                  <div key={key}>
                    <img src={item.list[key].imgSrc} alt="" />
                  </div>
                ) : (
                  <div
                    key={key}
                    className={`simple-list__body__item ${item.list[key].className ?? ''}`}
                  >
                    {item.list[key].value}
                  </div>
                )
              )}
              {mergeButtonIntoCell && selectButton}
            </td>
          );
        })}
        {!isDesktop && <td key="button">{selectButton}</td>}
      </tr>

      {!isAvailable ? (
        <tr className="simple-list__warning">
          <td colSpan={errorRowColSpan}>
            <ErrorResponse />
          </td>
        </tr>
      ) : null}
    </>
  );
};

type TableBodySmallestProps<L extends ListRowItem> = {
  listRows: SimpleListProps<L>['listRows'];
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  className?: string;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
};

export const TableBodySmallest = <L extends ListRowItem>(
  props: TableBodySmallestProps<L>
): JSX.Element => {
  const { listRows, className, ...listRowSmallestProps } = props;
  return (
    <TransitionGroup
      component="tbody"
      className={`simple-list__transition${className ? ' ' + className : ''}`}
    >
      {listRows.map((listRow, index) => (
        <CSSTransition key={listRow.id} nodeRef={listRow.nodeRef} timeout={400} classNames="fade">
          <ListRowSmallest
            {...listRowSmallestProps}
            listRows={listRows}
            listRow={listRow}
            key={listRow.id}
            index={index}
          />
        </CSSTransition>
      ))}
    </TransitionGroup>
  );
};

type ListRowSmallestProps<L extends ListRowItem> = {
  listRows: SimpleListProps<L>['listRows'];
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
};

const ListRowSmallest = <L extends ListRowItem>(
  props: ListRowSmallestProps<L> & { listRow: TableBodyProps<L>['listRows'][0]; index: number }
) => {
  const { listRow, listRows, onSelect: onSelectProp, checkAvailability, index } = props;
  const { ErrorResponse, isAvailable, onSelect, t, isLoading } = useTableRowHook({
    onSelect: onSelectProp,
    checkAvailability,
    index
  });

  return (
    <>
      <tr className={listRow.specialRow ? `simple-list__body__special ${listRow.groupSize}` : ''}>
        <td className={'simple-list__body__container'}>
          {Object.keys(listRow.item)
            .filter(key => !listRow.item[key].hideInMobile)
            .map(key =>
              listRow.item[key].imgSrc ? (
                <div key={key} className={`${listRow.item[key].className ?? ''}`}>
                  <img src={listRow.item[key].imgSrc} alt="" />
                </div>
              ) : (
                <div
                  key={key}
                  className={`simple-list__body__item ${listRow.item[key].className ?? ''}`}
                >
                  {listRow.item[key].value}
                </div>
              )
            )}
          <div key="button">
            <Button
              disabled={isLoading}
              color="primary"
              onClick={(): Promise<void> =>
                onSelect(
                  listRow.id,
                  listRow.code,
                  listRow.groupSize ?? '',
                  listRows.indexOf(listRow)
                )
              }
            >
              {t(Keys.select)}
            </Button>
          </div>
        </td>
      </tr>

      {!isAvailable ? (
        <tr className="simple-list__warning">
          <td>
            <ErrorResponse />
          </td>
        </tr>
      ) : null}
    </>
  );
};

const DefaultError = ({ scrollName }: { scrollName: string }) => {
  const { t } = useTranslation('simple-list', fallback, Keys);
  return (
    <ScrollTo name={scrollName}>
      <div className="info-name-badge mb-4">
        <FontAwesomeIcon icon={faInfoCircle} />
        <div>{t(Keys.unAvailableListItem)}</div>
      </div>
    </ScrollTo>
  );
};

type TableRowHookProps = {
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
  customRowError?: () => JSX.Element;
  index: number;
};

const useTableRowHook = ({
  onSelect: onSelectProp,
  checkAvailability,
  customRowError,
  index
}: TableRowHookProps) => {
  const [isAvailable, setIsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const SCROLL_ELEMENT_ERROR = 'list-item-unavailable' + index;

  const { t } = useTranslation('simple-list', fallback, Keys);

  const onSelect = async (id: string, code: string, groupSize?: string, indx?: number) => {
    if (checkAvailability) {
      setIsAvailable(true);
      setIsLoading(true);
      if (!(await checkAvailability(id, code, groupSize, indx))) {
        ScrollerService.scrollTo(SCROLL_ELEMENT_ERROR);
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    onSelectProp(id, code, groupSize, index);
  };
  const ErrorDef = () => <DefaultError scrollName={SCROLL_ELEMENT_ERROR} />;
  const ErrorResponse = customRowError || ErrorDef;

  return {
    onSelect,
    isAvailable,
    ErrorResponse,
    t,
    isLoading
  };
};
